import React from 'react';
import CommonBubble from '@home/components/CommonBubble';
import COSFileList from '@sg/components/COSFileList';
import DefaultGame from '@/pages/home/assets/img/common/game-default.svg';
import { mapBoardGovernanceStatus } from '@sg/pages/StudioManagement/config';
import type { Meeting, MeetingFile } from '@sg/pages/StudioManagement/BoardGovernanceNew/types';
import { renderRichText } from '@sg/utils/commonPageMethod';
import './index.scss';

interface MeetingCardProps {
  meeting: Meeting;
  isActive: boolean;
  onShare: (meeting: Meeting) => void;
  domain?: string;
  noStudio?: boolean;
}

const MeetingCard: React.FC<MeetingCardProps> = ({
  meeting,
  isActive,
  onShare,
  domain = 'mgmt/studio/board_governance',
  noStudio,
}) => {
  // Helper function to split filename and extension
  const splitFileName = (fileName?: string) => {
    if (!fileName || typeof fileName !== 'string') return { name: '', format: '' };
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex <= 0) {
      return { name: fileName, format: '' };
    }
    return {
      name: fileName.substring(0, lastDotIndex),
      format: fileName.substring(lastDotIndex) // Includes the dot
    };
  };

  // Transform files for COSFileList
  const transformFiles = (files: MeetingFile[]) => {
    return files.map((file: MeetingFile) => {
      // Derive a safe file name: prefer provided name, else fallback to basename(path)
      const rawName = (typeof file.file_name === 'string' && file.file_name)
        || (typeof file.url === 'string' && file.url.split('/').pop())
        || '';

      // Parse from name since we don't have explicit format
      const parsed = splitFileName(rawName);
      const fileNameWithoutExt = parsed.name || rawName;
      const fileExtension = parsed.format || '';

      // Ensure format doesn't have a dot at the beginning
      const cleanFormat = (fileExtension || '').startsWith('.') ? fileExtension.substring(1) : (fileExtension || '');

      return {
        path: file.url,
        name: fileNameWithoutExt,
        format: cleanFormat, // Without the dot
        size: 0,
      };
    });
  };

  return (
    <div className={`dbs-meeting-card ${isActive ? 'dbs--active' : ''}`}>
      <div className="dbs-meeting-figure">
        <div className="dbs-meeting-avatar">
          <img src={meeting.studio_cover || DefaultGame} alt="" />
        </div>
        {noStudio ?
          <div className="dbs-meeting-card-text">
            <div className="dbs-meeting-title">{meeting.title}</div>
            <div className="dbs-meeting-date">{meeting.date}</div>
          </div>
          : <div className="dbs-meeting-card-text">
            <div className="dbs-meeting-title">{meeting.studio_name}</div>
            <div className="dbs-meeting-desc">{meeting.title}</div>
            <div className="dbs-meeting-date">{meeting.date}</div>
          </div>
        }

        <div className="dbs-meeting-card-operate">
          <div className={`dbs-meeting-status --${mapBoardGovernanceStatus(meeting.status).toLowerCase()}`}>
            {mapBoardGovernanceStatus(meeting.status)}
          </div>
          <CommonBubble content="Share Meeting">
            <i
              className="dbs-meeting-share-icon"
              onClick={() => onShare(meeting)}
              style={{ cursor: 'pointer' }}
            ></i>
          </CommonBubble>
        </div>
      </div>

      <div className="dbs-meeting-summary">
        <div className="dbs-meeting-summary-title">{meeting.summary_title}</div>
        {/* <div
          className="dbs-meeting-summary-content"
          dangerouslySetInnerHTML={{ __html: meeting.description || '' }}
        /> */}
        {renderRichText(meeting.description || '', 'dbs-meeting-summary-content')}
        {meeting.files && meeting.files.length > 0 && (
          <COSFileList
            className="dbs-styled-cos-file-list"
            domain={domain}
            cosConfigDomain="mgmt/board_governance"
            urlSearchParams={{
              studio_id: meeting.studio_id,
              cos_domain: 'mgmt/board_governance',
              scroll_target: 'board_governance',
            }}
            enableActions={true}
            files={transformFiles(meeting.files)}
            fileItemWidth={320}
            visibleActions={{
              // preview: true,
              download: false,
              // share: true
            }}
          />
        )}
      </div>
    </div>
  );
};

export default MeetingCard;
